import { UiSchema } from '@rjsf/core';
import { JSONSchema7 } from 'json-schema';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { IntakeForm } from 'views/pages/client-record/useIntakeForm';
import JsonFormContainer from '../JsonFormContainer';
import Actions from './Actions';

const uiSchema: UiSchema = {
  healthInfo_1: { 'ui:widget': 'radio', 'ui:options': { inline: true } },
  healthInfo_3: { 'ui:widget': 'radio', 'ui:options': { inline: true } },
  healthInfo_4: { 'ui:widget': 'textarea' },
  healthInfo_6: { 'ui:widget': 'textarea' },
  healthInfo_7: { 'ui:widget': 'textarea' },
  healthInfo_8: { 'ui:widget': 'radio', 'ui:options': { inline: true } },
  healthInfo_9: { 'ui:widget': 'radio', 'ui:options': { inline: true } },
  healthInfo_12: { 'ui:widget': 'radio', 'ui:options': { inline: true } },
  healthInfo_13: { 'ui:widget': 'radio', 'ui:options': { inline: true } },
  healthInfo_14: { 'ui:widget': 'radio', 'ui:options': { inline: true } }
};

interface Props {
  intakeForm: IntakeForm;
  onPrevious?: () => void;
  isSubmitting: boolean;
  onSubmit: (data: any) => void;
}

export default function HealthAndSocialInformation({ intakeForm, onPrevious, isSubmitting, onSubmit }: Props) {
  const intl = useIntl();
  const schema: JSONSchema7 = {
    type: 'object',
    title: intl.formatMessage({ defaultMessage: 'Health And Social Information' }),
    properties: {
      healthInfo_1: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'Do you currently have a primary physician?' }),
        enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
      },
      healthInfo_2: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'If yes, who is it?' })
      },
      healthInfo_3: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'Are you currently seeing more than one medical health specialist?' }),
        enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
      },
      healthInfo_4: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'If yes, please list:' })
      },
      healthInfo_5: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'When was your last physical?' })
      },
      healthInfo_6: {
        type: 'string',
        title: intl.formatMessage({
          defaultMessage:
            'Please list any persistent physical symptoms or health concerns (e.g. chronic pain, headaches, hypertension, diabetes, etc.:'
        })
      },
      healthInfo_7: {
        type: 'string',
        title: intl.formatMessage({
          defaultMessage: 'Are you currently on medication to manage a physical health concern? If yes, please list:'
        })
      },
      healthInfo_8: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'Are you having any problems with your sleep habits?' }),
        enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
      },
      healthInfo_9: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'If yes, check where applicable:' }),
        enum: [
          intl.formatMessage({ defaultMessage: 'Sleeping too little' }),
          intl.formatMessage({ defaultMessage: 'Sleeping too much' }),
          intl.formatMessage({ defaultMessage: 'Poor quality sleep' }),
          intl.formatMessage({ defaultMessage: 'Disturbing dreams' }),
          intl.formatMessage({ defaultMessage: 'Other' })
        ]
      },
      healthInfo_10: {
        type: 'number',
        title: intl.formatMessage({ defaultMessage: 'How many times per week do you exercise?' })
      },
      healthInfo_11: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'Approximately how long each time?' })
      },
      healthInfo_12: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'Are you having any difficulty with appetite or eating habits?' }),
        enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
      },
      healthInfo_13: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'If yes, check where applicable:' }),
        enum: [
          intl.formatMessage({ defaultMessage: 'Eating less' }),
          intl.formatMessage({ defaultMessage: 'Eating more' }),
          intl.formatMessage({ defaultMessage: 'Bingeing' }),
          intl.formatMessage({ defaultMessage: 'Restricting' })
        ]
      },
      healthInfo_14: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'Have you experienced significant weight change in the last 2 months?' }),
        enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
      }
    }
  };
  const [formData, setFormData] = useState(intakeForm.healthInfo);

  return (
    <JsonFormContainer
      noValidate
      schema={schema}
      uiSchema={uiSchema}
      formData={formData}
      onChange={({ formData }) => setFormData(formData)}
      onSubmit={({ formData }) =>
        onSubmit({
          healthInfo: formData
        })
      }
    >
      <Actions onPrevious={onPrevious} disabled={isSubmitting} submitLabel={intl.formatMessage({ defaultMessage: 'Finish' })} />
    </JsonFormContainer>
  );
}
